import pluralize from "pluralize";
import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useMatch } from "react-router";

import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { type Group } from "../../graphql.js";
import { type ISavedGroupState, shouldLoadGroup } from "../../redux/modules/saved-group.js";
import { type SavedViewState } from "../../redux/modules/saved-view.js";
import { type IAppState } from "../../redux/reducer.js";
import { ContextWidgetType, type INavigationContextProps } from "../core/navigation-panel/ContextWidget.js";
import GroupPageLayout from "../core/navigation-panel/GroupPageLayout.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";

const getSavedGroupPageNavPanelCategory = (group: Group, absolutePath = false): INavPanelCategory => {
  return {
    label: group.name,
    navItems: [
      {
        label: pluralize.plural(group.typeDisplayName),
        badge: `${group.itemCount}`,
        route: `${absolutePath ? "/group/" : ""}${group.id}`,
        pageComponent: <GroupPageLayout group={group} />,
      },
    ],
  };
};

const SavedGroupPage: FC = () => {
  const viewId = useURLViewID();
  const match = useMatch("/group/:groupId/*");
  const comparisonMode = useMatch("/group/:groupId/comparison");
  const { group, isLoading } = useSelector<IAppState, ISavedGroupState>((state) => state.savedGroup);
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadGroup, match?.params.groupId);

  useEffect(() => {
    void load();
  }, [load]);

  const getContextProps = (): INavigationContextProps => {
    if (viewId && !isSavedViewLoading && view && Number.parseInt(view?.id) === viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: view,
      };
    } else if (!comparisonMode && !isLoading && group) {
      // if transientGroupTitle exists then we are not in a context of a saved group
      return {
        type: ContextWidgetType.SAVED_GROUP,
        data: group,
      };
    }
    return null;
  };

  return (
    <>
      {group && !isLoading && (
        <PageLayout
          contextWidgetProps={getContextProps()}
          navPanelCategories={[getSavedGroupPageNavPanelCategory(group)]}
        />
      )}
    </>
  );
};

export { getSavedGroupPageNavPanelCategory, SavedGroupPage };
